import { supabase } from '@/integrations/supabase/client';
import { TransactionsResponse } from '@/types/transaction';
import type {
  CardFeesByType,
  YearlyBreakdownRow,
  PaymentMethodBreakdown,
  AverageFeeMetrics,
} from '@/types/savingsCalculator';

export interface SavingsCalculatorMetricsPayload {
  totalCardFeesPaid: number;
  cardTransactionCount: number;
  cardFeesByType: CardFeesByType;
  yearlyBreakdown: YearlyBreakdownRow[];
  paymentMethodBreakdown: PaymentMethodBreakdown[];
  averageFeeMetrics: AverageFeeMetrics;
}

export async function fetchSavingsCalculatorData(
  accountId: string,
  createdSince: number
): Promise<SavingsCalculatorMetricsPayload> {
  const { data, error } = await supabase.functions.invoke('get-transactions', {
    body: { accountId, mode: 'savings', created_since: createdSince },
  });
  if (error) throw new Error(error.message || 'Failed to fetch savings calculator data');
  if (!data || typeof data !== 'object') throw new Error('No data returned from savings calculator');
  if (data.error) throw new Error(data.error);
  return data as SavingsCalculatorMetricsPayload;
}

export interface FetchTransactionsOptions {
  /** Use Stripe Search API  more performant for analytics. */
  useSearch?: boolean;
  createdSince?: number;
}

export async function fetchTransactions(
  accountId: string | null,
  limit: number,
  status?: string,
  cursor?: string,
  options?: FetchTransactionsOptions
): Promise<TransactionsResponse | null> {
  try {

    if (!accountId) {
      console.log('[Transaction Debug] No Stripe account ID found, skipping transaction fetch');
      return { transactions: [], has_more: false, next_cursor: null };
    }
    
    const requestId = `req_${Math.random().toString(36).substring(2, 10)}`;
    const body: Record<string, unknown> = {
      limit,
      starting_after: cursor,
      status,
      accountId,
      requestId
    };
    if (options?.useSearch) {
      body.useSearch = true;
      if (options.createdSince != null) body.created_since = options.createdSince;
    }

    const response = await supabase.functions.invoke('get-transactions', {
      body
    });
    
    // Check for error in response
    if (response.error) {
      console.error('[Transaction Debug] Supabase function error:', response.error);
      throw new Error(response.error.message || 'Failed to fetch transactions');
    }
    
    const data = response.data;
    
    // More detailed validation of response structure
    if (!data) {
      console.error('[Transaction Debug] No data returned from transaction service');
      throw new Error('No data returned from transaction service');
    }
    
    if (!Array.isArray(data.transactions)) {
      console.error('[Transaction Debug] Invalid response format - transactions is not an array:', data);
      throw new Error('Invalid response format from transaction service');
    }
    
    return data as TransactionsResponse;
  } catch (err: unknown) {
    if (err instanceof Error) {
      if (err.message.includes('Failed to fetch')) {
        console.error('[Transaction Debug] Network error in fetchTransactions:', err);
        throw new Error('Network error - please check your connection and try again');
      }
      console.error('[Transaction Debug] Error in fetchTransactions:', err);
      throw new Error(err.message || 'Failed to fetch transactions');
    } else {
      console.error('[Transaction Debug] Unknown error in fetchTransactions:', err);
      throw new Error('An unknown error occurred while fetching transactions');
    }
  }
}